import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const getAuthHeaders = () => {
  const token = localStorage.getItem('token');
  return token ? { Authorization: `Bearer ${token}` } : {};
};

// Categories
export const getCategories = async () => {
  const response = await axios.get(`${API}/categories`);
  return response.data;
};

// Neighborhoods
export const getNeighborhoods = async () => {
  const response = await axios.get(`${API}/neighborhoods`);
  return response.data;
};

// Zipcodes
export const getZipcodes = async () => {
  const response = await axios.get(`${API}/zipcodes`);
  return response.data;
};

// Nearby search
export const getNearbyItems = async (zipcode = null, latitude = null, longitude = null) => {
  const params = {};
  if (zipcode) params.zipcode = zipcode;
  if (latitude && longitude) {
    params.latitude = latitude;
    params.longitude = longitude;
  }
  const response = await axios.get(`${API}/nearby-users`, { params });
  return response.data;
};

// Items
export const getItems = async (params = {}) => {
  const response = await axios.get(`${API}/items`, { params });
  return response.data;
};

export const getItem = async (id) => {
  const response = await axios.get(`${API}/items/${id}`);
  return response.data;
};

export const createItem = async (data) => {
  const response = await axios.post(`${API}/items`, data, {
    headers: getAuthHeaders()
  });
  return response.data;
};

export const updateItem = async (id, data) => {
  const response = await axios.put(`${API}/items/${id}`, data, {
    headers: getAuthHeaders()
  });
  return response.data;
};

export const deleteItem = async (id) => {
  const response = await axios.delete(`${API}/items/${id}`, {
    headers: getAuthHeaders()
  });
  return response.data;
};

export const uploadItemImage = async (itemId, file) => {
  const formData = new FormData();
  formData.append('file', file);
  const response = await axios.post(`${API}/items/${itemId}/images`, formData, {
    headers: {
      ...getAuthHeaders(),
      'Content-Type': 'multipart/form-data'
    }
  });
  return response.data;
};

export const getMyItems = async () => {
  const response = await axios.get(`${API}/my-items`, {
    headers: getAuthHeaders()
  });
  return response.data;
};

export const getImageUrl = (path) => {
  if (!path) return null;
  return `${API}/files/${path}`;
};

// Rentals
export const createRental = async (data) => {
  const response = await axios.post(`${API}/rentals`, data, {
    headers: getAuthHeaders()
  });
  return response.data;
};

export const getRentals = async (role = null) => {
  const params = role ? { role } : {};
  const response = await axios.get(`${API}/rentals`, {
    params,
    headers: getAuthHeaders()
  });
  return response.data;
};

export const getRental = async (id) => {
  const response = await axios.get(`${API}/rentals/${id}`, {
    headers: getAuthHeaders()
  });
  return response.data;
};

export const updateRentalStatus = async (id, status) => {
  const response = await axios.put(`${API}/rentals/${id}/status`, null, {
    params: { status },
    headers: getAuthHeaders()
  });
  return response.data;
};

// Payments
export const createCheckout = async (rentalId) => {
  const response = await axios.post(`${API}/payments/checkout`, {
    rental_id: rentalId,
    origin_url: window.location.origin
  }, {
    headers: getAuthHeaders()
  });
  return response.data;
};

export const getPaymentStatus = async (sessionId) => {
  const response = await axios.get(`${API}/payments/status/${sessionId}`, {
    headers: getAuthHeaders()
  });
  return response.data;
};

// Messages
export const sendMessage = async (data) => {
  const response = await axios.post(`${API}/messages`, data, {
    headers: getAuthHeaders()
  });
  return response.data;
};

export const getConversations = async () => {
  const response = await axios.get(`${API}/messages/conversations`, {
    headers: getAuthHeaders()
  });
  return response.data;
};

export const getMessages = async (userId) => {
  const response = await axios.get(`${API}/messages/${userId}`, {
    headers: getAuthHeaders()
  });
  return response.data;
};

// Reviews
export const createReview = async (data) => {
  const response = await axios.post(`${API}/reviews`, data, {
    headers: getAuthHeaders()
  });
  return response.data;
};

export const getUserReviews = async (userId) => {
  const response = await axios.get(`${API}/reviews/user/${userId}`);
  return response.data;
};

// Users
export const getUser = async (userId) => {
  const response = await axios.get(`${API}/users/${userId}`);
  return response.data;
};

// Password Reset
export const forgotPassword = async (email) => {
  const response = await axios.post(`${API}/auth/forgot-password`, { email });
  return response.data;
};

export const resetPassword = async (token, newPassword) => {
  const response = await axios.post(`${API}/auth/reset-password`, { token, new_password: newPassword });
  return response.data;
};

// Notifications
export const getNotifications = async (unreadOnly = false) => {
  const response = await axios.get(`${API}/notifications`, {
    params: unreadOnly ? { unread_only: true } : {},
    headers: getAuthHeaders()
  });
  return response.data;
};

export const markNotificationRead = async (id) => {
  const response = await axios.put(`${API}/notifications/${id}/read`, null, { headers: getAuthHeaders() });
  return response.data;
};

export const markAllNotificationsRead = async () => {
  const response = await axios.put(`${API}/notifications/read-all`, null, { headers: getAuthHeaders() });
  return response.data;
};

// Wishlist
export const toggleWishlist = async (itemId) => {
  const response = await axios.post(`${API}/wishlist/${itemId}`, null, { headers: getAuthHeaders() });
  return response.data;
};

export const getWishlist = async () => {
  const response = await axios.get(`${API}/wishlist`, { headers: getAuthHeaders() });
  return response.data;
};

export const getWishlistIds = async () => {
  const response = await axios.get(`${API}/wishlist/ids`, { headers: getAuthHeaders() });
  return response.data;
};

// Item Calendar
export const getItemCalendar = async (itemId) => {
  const response = await axios.get(`${API}/items/${itemId}/calendar`);
  return response.data;
};

// Item Recommendations
export const getSimilarItems = async (itemId, limit = 4) => {
  const response = await axios.get(`${API}/items/${itemId}/similar`, { params: { limit } });
  return response.data;
};

export const getPopularItems = async (zipcode = null, limit = 8) => {
  const params = { limit };
  if (zipcode) params.zipcode = zipcode;
  const response = await axios.get(`${API}/items-popular`, { params });
  return response.data;
};

// Rental Export
export const exportRentals = async (format = 'csv') => {
  const response = await axios.get(`${API}/rentals/export`, {
    params: { format },
    headers: getAuthHeaders(),
    responseType: format === 'csv' ? 'blob' : 'json'
  });
  return response;
};

// Featured Listings
export const toggleFeatureItem = async (itemId) => {
  const response = await axios.post(`${API}/items/${itemId}/feature`, null, { headers: getAuthHeaders() });
  return response.data;
};

// Membership
export const getMembershipTiers = async () => {
  const response = await axios.get(`${API}/membership/tiers`);
  return response.data;
};

export const getMyMembership = async () => {
  const response = await axios.get(`${API}/membership/me`, { headers: getAuthHeaders() });
  return response.data;
};

export const subscribeMembership = async (tier) => {
  const response = await axios.post(`${API}/membership/subscribe`, null, {
    params: { tier },
    headers: getAuthHeaders()
  });
  return response.data;
};

export const cancelMembership = async () => {
  const response = await axios.post(`${API}/membership/cancel`, null, { headers: getAuthHeaders() });
  return response.data;
};

// Google Auth
export const googleAuthSession = async (sessionId) => {
  const response = await axios.post(`${API}/auth/google/session`, { session_id: sessionId });
  return response.data;
};

// Platform
export const getPlatformFee = async () => {
  const response = await axios.get(`${API}/platform/fee`);
  return response.data;
};

// Referral
export const getMyReferral = async () => {
  const response = await axios.get(`${API}/referral/me`, { headers: getAuthHeaders() });
  return response.data;
};

export const applyReferralCode = async (code) => {
  const response = await axios.post(`${API}/referral/apply`, null, {
    params: { code },
    headers: getAuthHeaders()
  });
  return response.data;
};

export const getReferralLeaderboard = async () => {
  const response = await axios.get(`${API}/referral/leaderboard`);
  return response.data;
};

// ID Verification
export const submitIdVerification = async (file) => {
  const formData = new FormData();
  formData.append('file', file);
  const response = await axios.post(`${API}/auth/verify-id`, formData, {
    headers: { ...getAuthHeaders(), 'Content-Type': 'multipart/form-data' }
  });
  return response.data;
};

export const getVerificationStatus = async () => {
  const response = await axios.get(`${API}/auth/verification-status`, { headers: getAuthHeaders() });
  return response.data;
};

// Saved Payment Methods
export const getPaymentMethods = async () => {
  const response = await axios.get(`${API}/payment-methods`, { headers: getAuthHeaders() });
  return response.data;
};

export const createCardSetupSession = async (originUrl) => {
  const response = await axios.post(`${API}/payment-methods/setup`, { origin_url: originUrl }, { headers: getAuthHeaders() });
  return response.data;
};

export const syncSavedCard = async (sessionId) => {
  const response = await axios.post(`${API}/payment-methods/sync`, { session_id: sessionId }, { headers: getAuthHeaders() });
  return response.data;
};

export const deletePaymentMethod = async (pmId) => {
  const response = await axios.delete(`${API}/payment-methods/${pmId}`, { headers: getAuthHeaders() });
  return response.data;
};

export const setDefaultPaymentMethod = async (pmId) => {
  const response = await axios.put(`${API}/payment-methods/${pmId}/default`, {}, { headers: getAuthHeaders() });
  return response.data;
};

// Rental Contract
export const getRentalContract = async () => {
  const response = await axios.get(`${API}/rental-contract`);
  return response.data;
};

// Insurance
export const getInsuranceOptions = async () => {
  const response = await axios.get(`${API}/insurance-options`);
  return response.data;
};

// Disputes
export const getDisputeReasons = async () => {
  const response = await axios.get(`${API}/disputes/reasons`);
  return response.data;
};

export const fileDispute = async (data) => {
  const response = await axios.post(`${API}/disputes`, data, { headers: getAuthHeaders() });
  return response.data;
};

export const getMyDisputes = async () => {
  const response = await axios.get(`${API}/disputes`, { headers: getAuthHeaders() });
  return response.data;
};

export const getDispute = async (id) => {
  const response = await axios.get(`${API}/disputes/${id}`, { headers: getAuthHeaders() });
  return response.data;
};

export const respondToDispute = async (id, responseText) => {
  const response = await axios.post(`${API}/disputes/${id}/respond`, null, {
    params: { response: responseText },
    headers: getAuthHeaders()
  });
  return response.data;
};

export const resolveDispute = async (id, resolution, payoutAmount = 0, adminNotes = '') => {
  const response = await axios.put(`${API}/disputes/${id}/resolve`, null, {
    params: { resolution, payout_amount: payoutAmount, admin_notes: adminNotes },
    headers: getAuthHeaders()
  });
  return response.data;
};

export const dismissDispute = async (id, adminNotes = '') => {
  const response = await axios.put(`${API}/disputes/${id}/dismiss`, null, {
    params: { admin_notes: adminNotes },
    headers: getAuthHeaders()
  });
  return response.data;
};

export const getAdminDisputes = async (status = null) => {
  const params = status ? { status } : {};
  const response = await axios.get(`${API}/admin/disputes`, { params, headers: getAuthHeaders() });
  return response.data;
};

export default {
  getCategories,
  getNeighborhoods,
  getItems,
  getItem,
  createItem,
  updateItem,
  deleteItem,
  uploadItemImage,
  getMyItems,
  getImageUrl,
  createRental,
  getRentals,
  getRental,
  updateRentalStatus,
  createCheckout,
  getPaymentStatus,
  sendMessage,
  getConversations,
  getMessages,
  createReview,
  getUserReviews,
  getUser
};
